import { analyticsService } from '../index-business';

export interface StudioEventData {
  studioId?: string;
  ownerId?: string;
  studio?: any;
  repeatableEvent?: any;
  scheduledEvent?: any;
  room?: any;
  [key: string]: any;
}

export class StudioTracker {
  static trackCreateStudio(data: { studio: any; success: boolean; error?: string; duration?: number; result?: any }) {
    const eventData = {
      studio_name: data.studio?.name,
      studio_category: data.studio?.category,
      studio_subcategory: data.studio?.subcategory,
      studio_type: data.studio?.type,
      studio_address: data.studio?.address ? 'has_address' : 'no_address',
      studio_phone: data.studio?.phone ? 'has_phone' : 'no_phone',
      studio_email: data.studio?.email ? 'has_email' : 'no_email',
      studio_website: data.studio?.website ? 'has_website' : 'no_website',
      studio_description: data.studio?.description ? 'has_description' : 'no_description',
      description_length: data.studio?.description?.length || 0,
      has_avatar: data.studio?.avatar ? true : false,
      has_background: data.studio?.background ? true : false,
      has_gallery: data.studio?.gallery?.length > 0 || false,
      gallery_count: data.studio?.gallery?.length || 0,
      studio_id: data.result?._id || data.result?.id,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'createStudio',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Studio Created', eventData);
  }

  static trackUpdateStudio(data: {
    studio: any;
    workingHours?: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      studio_id: data.studio?._id || data.studio?.id,
      studio_name: data.studio?.name,
      studio_category: data.studio?.category,
      studio_subcategory: data.studio?.subcategory,
      has_working_hours_update: data.workingHours ? true : false,
      working_days_count: data.workingHours?.days?.length || 0,
      studio_address: data.studio?.address ? 'has_address' : 'no_address',
      studio_phone: data.studio?.phone ? 'has_phone' : 'no_phone',
      studio_email: data.studio?.email ? 'has_email' : 'no_email',
      studio_website: data.studio?.website ? 'has_website' : 'no_website',
      has_avatar: data.studio?.avatar ? true : false,
      has_background: data.studio?.background ? true : false,
      gallery_count: data.studio?.gallery?.length || 0,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'updateStudio',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Studio Updated', eventData);
  }

  static trackDeleteStudio(data: { studioId: string; success: boolean; error?: string; duration?: number }) {
    const eventData = {
      studio_id: data.studioId,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'deleteStudioById',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Studio Deleted', eventData);
  }

  // === COURSE/EVENT MANAGEMENT ===

  static trackAddRepeatableEvent(data: {
    studioId: string;
    repeatableEvent: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      studio_id: data.studioId,
      event_name: data.repeatableEvent?.name,
      event_type: data.repeatableEvent?.type,
      event_category: data.repeatableEvent?.category,
      event_duration: data.repeatableEvent?.duration,
      event_price: data.repeatableEvent?.price,
      event_capacity: data.repeatableEvent?.capacity,
      has_description: data.repeatableEvent?.description ? true : false,
      description_length: data.repeatableEvent?.description?.length || 0,
      repeat_pattern: data.repeatableEvent?.repeatPattern,
      selected_days: data.repeatableEvent?.selectedDays || [],
      selected_days_count: data.repeatableEvent?.selectedDays?.length || 0,
      has_teacher: data.repeatableEvent?.teacher ? true : false,
      has_room: data.repeatableEvent?.room ? true : false,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addRepeatableEvent',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Course Created', eventData);
  }

  static trackUpdateRepeatableEvent(data: {
    studioId: string;
    repeatableEventId: string;
    repeatableEvent: any;
    updateEventsFields: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const updatedFields = data.updateEventsFields ? Object.keys(data.updateEventsFields) : [];

    const eventData = {
      studio_id: data.studioId,
      repeatable_event_id: data.repeatableEventId,
      event_name: data.repeatableEvent?.name,
      event_type: data.repeatableEvent?.type,
      updated_fields: updatedFields,
      updates_scheduled_events: updatedFields.length > 0,
      event_duration: data.repeatableEvent?.duration,
      event_price: data.repeatableEvent?.price,
      event_capacity: data.repeatableEvent?.capacity,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'updateRepeatableEvent',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Course Updated', eventData);
  }

  static trackDeleteRepeatableEvent(data: {
    studioId: string;
    repeatableEventId: string;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      studio_id: data.studioId,
      repeatable_event_id: data.repeatableEventId,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'deleteRepeatableEvent',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Course Deleted', eventData);
  }

  static trackRestoreCourse(data: {
    studioId: string;
    courseId: string;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      studio_id: data.studioId,
      course_id: data.courseId,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'restoreCourse',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Course Restored', eventData);
  }

  // === SCHEDULED EVENTS ===

  static trackAddScheduledEvent(data: {
    studioId: string;
    repeatableEventId: string;
    scheduledEvent: any;
    newSelectedDays?: string[];
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      studio_id: data.studioId,
      repeatable_event_id: data.repeatableEventId,
      scheduled_date: data.scheduledEvent?.date,
      scheduled_time: data.scheduledEvent?.time,
      event_duration: data.scheduledEvent?.duration,
      has_teacher: data.scheduledEvent?.teacher ? true : false,
      has_room: data.scheduledEvent?.room ? true : false,
      new_selected_days: data.newSelectedDays || [],
      new_selected_days_count: data.newSelectedDays?.length || 0,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addScheduledEvent',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Class Added', eventData);
  }

  static trackDeleteScheduledEvent(data: {
    studioId: string;
    eventId: string;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      studio_id: data.studioId,
      scheduled_event_id: data.eventId,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'deleteScheduledEvent',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Class Deleted', eventData);
  }

  // === ROOM MANAGEMENT ===

  static trackAddRoom(data: { studioId: string; room: any; success: boolean; error?: string; duration?: number }) {
    const eventData = {
      studio_id: data.studioId,
      room_name: data.room?.name,
      room_capacity: data.room?.capacity,
      room_description: data.room?.description ? 'has_description' : 'no_description',
      description_length: data.room?.description?.length || 0,
      has_equipment: data.room?.equipment?.length > 0 || false,
      equipment_count: data.room?.equipment?.length || 0,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addRoom',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Room Created', eventData);
  }

  static trackUpdateRoom(data: { studioId: string; room: any; success: boolean; error?: string; duration?: number }) {
    const eventData = {
      studio_id: data.studioId,
      room_id: data.room?._id,
      room_name: data.room?.name,
      room_capacity: data.room?.capacity,
      room_description: data.room?.description ? 'has_description' : 'no_description',
      has_equipment: data.room?.equipment?.length > 0 || false,
      equipment_count: data.room?.equipment?.length || 0,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'updateRoom',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Room Updated', eventData);
  }

  static trackDeleteRoom(data: {
    studioId: string;
    roomId: string;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      studio_id: data.studioId,
      room_id: data.roomId,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'deleteRoom',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Room Deleted', eventData);
  }

  static trackStudioApiCall(
    endpoint: string,
    data: StudioEventData & {
      success: boolean;
      error?: string;
      duration?: number;
    }
  ) {
    const eventData = {
      ...data,
      event_type: 'api_call',
      api_endpoint: endpoint,
      timestamp: new Date().toISOString(),
    };

    analyticsService.track(`Studio API: ${endpoint}`, eventData);
  }
}

export const trackStudioSuccess = (endpoint: string, data: StudioEventData, duration?: number) => {
  StudioTracker.trackStudioApiCall(endpoint, {
    ...data,
    success: true,
    duration,
  });
};

export const trackStudioError = (endpoint: string, error: string, data: StudioEventData, duration?: number) => {
  StudioTracker.trackStudioApiCall(endpoint, {
    ...data,
    success: false,
    error,
    duration,
  });
};

export default StudioTracker;
